import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';

import { breakpoints } from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToStep } from '@service-initiation/shared-data-access-flow-manager';
import { AddToCalendar } from '@service-initiation/shared-ui-add-to-calendar';
import {
  topImageDesktop,
  topImageMobile,
} from '@service-initiation/shared-ui-assets';
import { useIsDesktop } from '@service-initiation/shared-ui-breakpoints';
import { CheckCircleIcon } from '@service-initiation/shared-ui-check-circle-icon';
import { NoAvailabilityModal } from '@service-initiation/shared-ui-no-availability-modal';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { useAsyncAtom } from '@service-initiation/shared-utils-jotai';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import {
  selectedAppointmentAsyncAtom,
  useCustomerDetailsAtom,
  useCustomerDetailsConfirmedAtom,
  useLeadIdValue,
  useResetSelectedAppointment,
  useSelectedStoreAsyncAtom,
  useSetAlertBannerAtom,
} from '@service-initiation/tech-care-data-access-store';
import {
  ErrorType,
  invalidateCachedOpenAppointments,
} from '@service-initiation/tech-care-data-access-tech-care-services';
import { CallButton } from '@service-initiation/tech-care-ui-call-button';
import { GetDirections } from '@service-initiation/tech-care-ui-get-directions';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';
import { WhatHappensNext } from '@service-initiation/tech-care-ui-what-happens-next';

import { useCreateAppointment } from '../../hooks';

const Container = styled.div<{ show: boolean }>`
  text-align: left;
  margin-bottom: 40px;
  opacity: ${({ show }) => (show ? '1' : '0')};
  transition: opacity 200ms linear;
`;

const TopImage = styled.img`
  width: 100%;
`;

const MarginTopWrapper = styled.div`
  margin-top: 48px;
`;

const QuickActionsWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  margin-top: 24px;
  flex-direction: row;
  gap: 12px;

  @media screen and (${breakpoints.smMax}) {
    button,
    a {
      width: fill-available;
    }
  }
  ${breakpoints.md2} {
    button,
    a {
      flex: 1;
    }
  }
`;

const TEXT = TranslationEntries.Repairs.Confirmation;
const ERROR_TEXT = TranslationEntries.Repairs.Error;
const CUSTOMER_DETAILS_PATHNAME = '/tech-care/repairs/customer-details';
const TIME_TAKEN_ERROR = TranslationEntries.Repairs.TimeTakenError;

export const VisitStoreConfirmationPage = () => {
  const { t } = useTranslation();
  const { value: appointment } = useAsyncAtom(selectedAppointmentAsyncAtom);
  const { value: selectedStore } = useSelectedStoreAsyncAtom();
  const { goToStep } = useGoToStep();

  const [customerDetails] = useCustomerDetailsAtom();
  const leadId = useLeadIdValue();
  const [, setCustomerDetailsConfirmed] = useCustomerDetailsConfirmedAtom();
  const setAlertBanner = useSetAlertBannerAtom();
  const resetSelectedAppointment = useResetSelectedAppointment();
  const [topImageLoaded, setTopImageLoaded] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const { dispatchClickEvent, dispatchViewEvent } = useAnalytics();
  const { ref: elementRef } = useInViewAnalytics(
    'VisitStoreConfirmation',
    {
      ...(customerDetails?.phoneNumber && {
        mdn: customerDetails?.phoneNumber,
      }),
      CompletedFlow: true,

      marketingCommunicationsOptIn:
        customerDetails?.agreeToEmailOffers || false,
    },
    {
      Email: customerDetails?.email || '',
    }
  );
  const [
    { error: createAppointmentError, loading: createAppointmentLoading },
    createAppointmentFn,
  ] = useCreateAppointment();

  const reselectAppointmentTime = useCallback(
    (cta: string) => {
      invalidateCachedOpenAppointments();
      resetSelectedAppointment();
      setCustomerDetailsConfirmed(false);
      dispatchClickEvent('GoToTimeSlot', {
        Cta: cta,
        ErrorType: 'SlotTaken',
      });
      goToStep(Route.AppointmentTime);
    },
    [
      dispatchClickEvent,
      goToStep,
      resetSelectedAppointment,
      setCustomerDetailsConfirmed,
    ]
  );

  useEffectOnce(() => {
    if (!leadId) {
      createAppointmentFn();
    }
  });

  useEffect(() => {
    if (
      createAppointmentError &&
      createAppointmentError.message !== ErrorType.SlotIsTakenError
    ) {
      setAlertBanner({
        bannerType: 'error',
        message: t(ERROR_TEXT.GenericError),
        skipResetOnPaths: [CUSTOMER_DETAILS_PATHNAME],
      });
      dispatchViewEvent('AlertBanner', {
        error: createAppointmentError,
        message: t(ERROR_TEXT.GenericError),
      });
      setCustomerDetailsConfirmed(false);
      goToStep(Route.CustomerDetails, { replace: true });
    }
  }, [
    createAppointmentError,
    customerDetails,
    dispatchViewEvent,
    goToStep,
    setAlertBanner,
    setCustomerDetailsConfirmed,
    t,
  ]);

  return createAppointmentError &&
    createAppointmentError.message === ErrorType.SlotIsTakenError ? (
    <NoAvailabilityModal
      dataTestId="no-availability-modal"
      isOpen={true}
      onButtonClick={() =>
        reselectAppointmentTime(t(TIME_TAKEN_ERROR.Button.Text))
      }
      onCloseClick={() => reselectAppointmentTime('CloseX')}
      primaryButtonLabel={t(TIME_TAKEN_ERROR.Button.Text)}
      subtitle={t(TIME_TAKEN_ERROR.Text)}
      title={t(TIME_TAKEN_ERROR.Title)}
    />
  ) : (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          {!createAppointmentLoading ? (
            <Container
              data-testid="confirmation-container"
              ref={elementRef}
              show={topImageLoaded}
            >
              <CheckCircleIcon />
              <StepTitle
                subtitle={t(TEXT.Subtitle)}
                title={
                  <Trans
                    components={{
                      Pii: <span className="fs-mask" />,
                    }}
                    data-testid="confirmation-title"
                    i18nKey={TEXT.Title}
                    values={{ firstName: customerDetails?.firstName }}
                  />
                }
              />
              <TopImage
                alt=""
                data-testid="confirmation-top-image"
                onLoad={() => setTopImageLoaded(true)}
                src={isDesktop ? topImageDesktop : topImageMobile}
              />
              <QuickActionsWrapper>
                <AddToCalendar
                  buttonText={t(TEXT.AddToCalendar)}
                  endTime={appointment?.endTime}
                  location={selectedStore?.address}
                  name={`Device Repair: ${selectedStore?.name}`}
                  onClick={() => {
                    dispatchClickEvent('AddToCalendar', {
                      Cta: t(TEXT.AddToCalendar),
                    });
                  }}
                  startDate={appointment?.date}
                  startTime={appointment?.startTime}
                />
                <CallButton
                  contactNumber={selectedStore?.phone}
                  label={selectedStore?.phone || undefined}
                />
                <GetDirections address={selectedStore?.address} />
              </QuickActionsWrapper>
              <MarginTopWrapper tabIndex={0}>
                <WhatHappensNext />
              </MarginTopWrapper>
            </Container>
          ) : (
            <StepContentSkeleton />
          )}
        </LeftMainContentSection>
      </MainContentContainer>
    </PageContainer>
  );
};
